import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../context/GlobalState';
import { deusSocket, sendWithAuth } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { initializeCaptcha, resetCaptcha } from '../utils/captcha';
import './Pricing.css';

function Pricing() {
  const { userIsSignedIn, user } = useGlobalState();
  const [isLoading, setIsLoading] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [selectedTier, setSelectedTier] = useState('');
  const [emailError, setEmailError] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const navigate = useNavigate();

  // Load the reCAPTCHA script using the shared utility
  useEffect(() => {
    let cleanup = null;
    
    if (showEmailInput) {
      cleanup = initializeCaptcha(
        // On token change
        (token) => setCaptchaToken(token),
        // On error
        (errorMsg) => setEmailError(errorMsg)
      );
    }
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [showEmailInput]);

  const handleTierSelect = (tier) => {
    if (userIsSignedIn) {
      // If user is already signed in, update their membership and navigate to app
      updateMembership(tier);
    } else {
      // If user is not signed in, show the email input form
      setSelectedTier(tier);
      setShowEmailInput(true);
    }
  };

  const updateMembership = (membership) => {
    setIsLoading(true);

    // If already signed in, update user's membership
    if (user && user._id) {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('user');
      
      if (!token || !userId) {
        setIsLoading(false);
        alert('Please sign in to update your membership.');
        navigate('/auth');
        return;
      }

      const auth = { token, userId };
      const payload = {
        _id: user._id,
        membership
      };

      sendWithAuth('updateUserProfile', payload, auth);
      
      // Handle response
      const handleUpdateResponse = (userData) => {
        setIsLoading(false);
        navigate('/app');
        deusSocket.off('deliverUpdateProfileRes', handleUpdateResponse);
        deusSocket.off('updateUserFailure', handleError);
      };

      const handleError = (err) => {
        setIsLoading(false);
        alert(err.message);
        deusSocket.off('deliverUpdateProfileRes', handleUpdateResponse);
        deusSocket.off('updateUserFailure', handleError);
      };

      deusSocket.on('deliverUpdateProfileRes', handleUpdateResponse);
      deusSocket.on('updateUserFailure', handleError);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    
    // Basic email validation
    if (!signupEmail || !signupEmail.includes('@') || !signupEmail.includes('.')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Verify captcha is completed
    if (!captchaToken) {
      setEmailError('Please complete the captcha verification');
      return;
    }
    
    setIsLoading(true);
    setEmailError('');
    
    // Send signup request with email, membership tier, and captcha token
    deusSocket.emit('userSignUp', {
      email: signupEmail,
      membership: selectedTier,
      captchaToken: captchaToken
    });
    
    // Handle response
    const handleSignupSuccess = (userData) => {
      localStorage.setItem('token', userData.token);
      localStorage.setItem('user', userData._id.toString());
      setIsLoading(false);
      
      // Show verification message in the UI instead of an alert
      setVerificationMessage(
        'Please check your email for a verification link. ' +
        'When you click the verification link, it will open in a new tab where you will be logged in.'
      );
      
      // Remove immediate navigation - we'll let the user navigate after seeing the message
      // navigate('/auth');
      
      deusSocket.off('deliverUserSignUp', handleSignupSuccess);
      deusSocket.off('error-event', handleSignupError);
    };
    
    const handleSignupError = (err) => {
      setIsLoading(false);
      
      // Handle specific error case for verified users
      if (err.message === 'VERIFIED_USER') {
        // User exists and is verified, direct them to login
        setEmailError(
          <div className="email-error-with-action">
            <p>This email is already registered.</p>
            <button 
              className="error-action-btn"
              onClick={() => navigate('/auth')}
            >
              Go to login page
            </button>
          </div>
        );
      } else {
        // Handle other errors normally
        setEmailError(err.message);
      }
      
      resetCaptcha(); // Use shared resetCaptcha
      deusSocket.off('deliverUserSignUp', handleSignupSuccess);
      deusSocket.off('error-event', handleSignupError);
    };
    
    deusSocket.on('deliverUserSignUp', handleSignupSuccess);
    deusSocket.on('error-event', handleSignupError);
  };

  return (
    <div className="pricing-page public-page-content">
      {isLoading && (
        <div className="loading-overlay">
          <LoadingSpinner />
          <p>Processing your request...</p>
        </div>
      )}
      
      {verificationMessage ? (
        <div className="verification-message-container">
          <div className="verification-message">
            <div className="verification-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#2e7d32" width="48px" height="48px">
                <path d="M0 0h24v24H0V0z" fill="none"/>
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-.4 4.25l-7.07 4.42c-.32.2-.74.2-1.06 0L4.4 8.25c-.25-.16-.4-.43-.4-.72 0-.67.73-1.07 1.3-.72L12 11l6.7-4.19c.57-.35 1.3.05 1.3.72 0 .29-.15.56-.4.72z"/>
              </svg>
            </div>
            <h2>Email Verification Sent</h2>
            <p>{verificationMessage}</p>
            <div className="verification-actions">
              <button 
                className="primary-button"
                onClick={() => navigate('/auth')}
              >
                Go to Login Page
              </button>
              <button
                className="secondary-button"
                onClick={() => window.location.reload()}
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      ) : showEmailInput ? (
        <div className="email-signup-container">
          <h2>Almost there!</h2>
          <p>Please enter your email to continue with the {selectedTier} plan</p>
          
          <form onSubmit={handleEmailSubmit} className="email-signup-form">
            <div className="form-group">
              <input
                type="email"
                placeholder="Your email address"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                required
              />
              {emailError && <div className="email-error">{emailError}</div>}
            </div>
            {/* Google reCAPTCHA */}
            <div className="captcha-container">
              <div 
                id="g-recaptcha-container"
                className="g-recaptcha" 
                data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} 
                data-callback="onCaptchaComplete"
                data-error-callback="onCaptchaError"
              ></div>
              {!captchaToken && emailError && emailError.includes('captcha') && (
                <div className="captcha-error">Please complete the captcha verification</div>
              )}
            </div>

            <button type="submit" className="signup-submit-btn">Continue</button>
            <button 
              type="button" 
              className="back-to-pricing-btn"
              onClick={() => {
                setShowEmailInput(false);
                resetCaptcha();
              }}
            >
              Back to Pricing
            </button>
          </form>
        </div>
      ) : (
        <>
          <div className="pricing-header">
            <h1>Choose Your Plan</h1>
            <p>Select the plan that best suits your needs</p>
          </div>

          <div className="pricing-tiers">
            {/* Free Tier */}
            <div className="pricing-tier">
              <div className="tier-header">
                <h2>Free</h2>
                <div className="price">$0<span>/month</span></div>
              </div>
              <div className="tier-features">
                <ul>
                  <li>Create 1 mind clone</li>
                  <li>Unlimited interview training</li>
                  <li>Unlimited chat interactions</li>
                  <li>Automated fine-tuning</li>
                  <li>Export conversations</li>
                  <li>Export character profiles</li>
                  <li>Create custom agent profiles</li>
                  {/* <li>Basic fine-tuning</li> */}
                  {/* <li>Daily capped chat interactions</li> */}
                  {/* <li>Limited knowledge uploads</li> */}
                </ul>
              </div>
              <div className="tier-cta">
                <button onClick={() => handleTierSelect('free')} className="tier-button free">Get Started</button>
              </div>
            </div>

            {/* Starter Tier */}
            {/* <div className="pricing-tier highlighted">
              <div className="most-popular">Most Popular</div>
              <div className="tier-header">
                <h2>Starter</h2>
                <div className="price">$10<span>/month</span></div>
              </div>
              <div className="tier-features">
                <ul>
                  <li>Create up to 5 mind clones</li>
                  <li>Unlimited interview training</li>
                  <li>Export conversations</li>
                  <li>Premium fine-tuning</li>
                  <li>Unlimited chat interactions</li>
                  <li>Unlimited knowledge uploads</li>
                  <li>Agent customization</li>
                  <li>Export character profiles</li>
                </ul>
              </div>
              <div className="tier-cta">
                <button onClick={() => handleTierSelect('plus')} className="tier-button pro">Choose Starter</button>
              </div>
            </div> */}

            {/* Pro Tier */}
            <div className="pricing-tier">
              <div className="tier-header">
                <h2>Pro</h2>
                <div className="price">$10<span>/month</span></div>
              </div>
              <div className="tier-features">
                <ul>
                  <li>Create unlimited mind clones</li>
                  <li>Unlimited interview training</li>
                  <li>Unlimited chat interactions</li>
                  <li>Automated fine-tuning</li>
                  <li>Export conversations</li>
                  <li>Export character profiles</li>
                  <li>Create custom agent profiles</li>
                  <li>Premium fine-tuning on demand</li>
                  {/* <li>Unlimited knowledge uploads</li> */}
                  {/* <li>Agent customization</li> */}
                </ul>
              </div>
              <div className="tier-cta">
                {/* <button onClick={() => handleTierSelect('pro')} className="tier-button enterprise">Choose Pro</button> */}
                <p className="coming-soon-text">Coming Soon</p>
              </div>
            </div>

            {/* God Tier */}
            {/* <div className="pricing-tier">
              <div className="tier-header">
                <h2>God</h2>
                <div className="price">$100<span>/month</span></div>
              </div>
              <div className="tier-features">
                <ul>
                  <li>Create unlimited mind clones</li>
                  <li>Unlimited interview training</li>
                  <li>Export conversations</li>
                  <li>Premium fine-tuning</li>
                  <li>Unlimited chat interactions</li>
                  <li>Unlimited knowledge uploads</li>
                  <li>Agent customization</li>
                  <li>Export character profiles</li>
                </ul>
              </div>
              <div className="tier-cta">
                <button onClick={() => handleTierSelect('god')} className="tier-button enterprise">Choose God</button>
              </div>
            </div> */}
          </div>

          <div className="pricing-faq">
            <h2>Frequently Asked Questions</h2>
            
            <div className="faq-item">
              <h3>What is a mind clone?</h3>
              <p>A mind clone is an AI-powered digital replica of your personality, thought processes, and communication style. It learns from your responses during interviews and becomes more accurate as you train it.</p>
            </div>
            
            <div className="faq-item">
              <h3>How long does it take to create a mind clone?</h3>
              <p>V1 of your mind clone is unlocked after answering 20 questions, but your clone becomes more accurate the more questions you answer.</p>
            </div>
            
            {/* <div className="faq-item">
              <h3>Can I upgrade or downgrade my plan later?</h3>
              <p>Yes, you can change your subscription at any time. If you downgrade, you'll maintain access to your primary mind clone, but additional clones will become inactive.</p>
            </div> */}
          </div>

          <div className="pricing-cta">
            <h2>Ready to create your digital twin?</h2>
            <p>Start with our free plan and upgrade anytime.</p>
            <button onClick={() => handleTierSelect('free')} className="primary-button">Get Started Now</button>
          </div>
        </>
      )}
    </div>
  );
}

export default Pricing; 